import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruckFast } from "@fortawesome/free-solid-svg-icons";

import ImageSliderHook from "../hooks/ImageSilderHook";
import {
  updateCartItemQuantity,
  setCartItemQuantity,
  addCustomDesign,
  removeCustomDesign,
  updateCustomDesignImage,
  updateCustomDesignQuantity,
  setCartOpen,
} from "../actions/cartActions";
import useComponentVisible from "../hooks/useComponentVisible";

const OrderQRCorall = ({
  modalRef,
  setIsVisable,
  productType = "qr-collar", // "qr-collar" or "gps-tracker"
}) => {
  const location = useLocation();
  const dispatch = useDispatch();

  // Determine product type from URL or prop
  const isGPSTracker =
    productType === "gps-tracker" || location.pathname.includes("gpstracker");
  const currentProductType = isGPSTracker ? "gps-tracker" : "qr-collar";

  // Get cart state from Redux based on product type
  const cartState = useSelector((state) => state.cart);
  const currentCart = isGPSTracker ? cartState.gpsTracker : cartState.qrCollar;
  const colorQuantities = currentCart.colorQuantities;
  const customDesigns = currentCart.customDesigns;

  const qrCollarImages = [
    "/img/1-1.webp",
    "/img/1-2.webp",
    "/img/1-3.webp",
    "/img/1-4.webp",
    "/img/1-5.webp",
    "/img/DogWithTag.webp",
    "/img/PethubTags.webp",
  ];

  // GPS Tracker images (using same images for now, will be changed later)
  const gpsTrackerImages = [
    "/img/circle-yellow-1.webp",
    "/img/yellow.webp",
    "/img/circle-blue-1.webp",
    "/img/scratching.webp",
    "/img/circle-gray-1.webp",
    "/img/sleeping.webp",
    "/img/circle-pink-1.webp",
    "/img/activity.webp",
  ];

  const images = isGPSTracker ? gpsTrackerImages : qrCollarImages;

  // Pricing based on product type
  const unitPrice = isGPSTracker ? 205 : 33;

  const {
    currentImageIndex,
    setCurrentImageIndex,
    handleNextImage,
    handlePreviousImage,
  } = ImageSliderHook(images, 0);
  const [
    collarDescriptionRef,
    isCollarDescriptionVisalbe,
    setIsCollarDescriptionVisable,
  ] = useComponentVisible(false);
  const [collarSizeFitRef, isCollarSizeFitVisalbe, setIsCollarSizeFitVisable] =
    useComponentVisible(false);
  const [petProfileRef, isPetProfileVisalbe, setIsPetProfileVisable] =
    useComponentVisible(false);
  const [tagFeaturesRef, isTagFeaturesVisalbe, setIsTagFeaturesVisable] =
    useComponentVisible(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = () => {
    navigate(-1);
  };

  // Helper functions for quantity management - now using Redux
  const handleQuantityChange = (color, increment) => {
    dispatch(updateCartItemQuantity(currentProductType, color, increment));
  };

  const handleQuantityInput = (color, value) => {
    const numValue = parseInt(value) || 0;
    dispatch(setCartItemQuantity(currentProductType, color, numValue));
  };

  // Helper functions for custom designs - now using Redux
  const handleAddCustomDesign = () => {
    dispatch(addCustomDesign(currentProductType));
  };

  const handleRemoveCustomDesign = (id) => {
    dispatch(removeCustomDesign(currentProductType, id));
  };

  const handleUpdateCustomDesignImage = (id, file) => {
    const preview = URL.createObjectURL(file);
    // Convert File to base64 so it survives localStorage JSON serialization
    const reader = new FileReader();
    reader.onload = () => {
      dispatch(
        updateCustomDesignImage(currentProductType, id, reader.result, preview),
      );
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateCustomDesignQuantity = (id, quantity) => {
    const numValue = parseInt(quantity) || 0;
    dispatch(updateCustomDesignQuantity(currentProductType, id, numValue));
  };

  const renderImages = () => {
    return images.map((image, index) => {
      return (
        <div
          key={index}
          className="img-box"
          onClick={() => setCurrentImageIndex(index)}
        >
          <img src={image} alt="" />
        </div>
      );
    });
  };

  // ითვლის სტანდარტული ფერების ჯამს
  const standardItemsCount = Object.values(colorQuantities).reduce(
    (sum, qty) => sum + qty,
    0,
  );

  // ითვლის ინდივიდუალური დიზაინების ჯამს (მხოლოდ იმ შემთხვევაში, თუ სურათი ატვირთულია და რაოდენობა > 0)
  const customItemsCount = customDesigns
    .filter((d) => (d.preview || d.image) && d.quantity > 0)
    .reduce((sum, d) => sum + (d.quantity || 0), 0);

  const totalSelectedItems = standardItemsCount + customItemsCount;

  const renderColor = () => {
    return (
      <>
        <div className="select-color-container">
          <div className="select-color">
            <p className="select-color-text">
              {t("pick your color and quantity")}
            </p>
            <div className="color-button-container">
              <div className="color-container-with-quantity orange">
                <button
                  type="button"
                  className="orange"
                  data-title={t("orange")}
                  style={{ pointerEvents: "none", cursor: "default" }}
                >
                  <span>{t("orange")}</span>
                </button>
                <div className="quantity-counter">
                  <div
                    className="quantity-decrease-button"
                    onClick={() => handleQuantityChange("orange", -1)}
                  >
                    -
                  </div>
                  <div
                    className="quantity-increase-button"
                    onClick={() => handleQuantityChange("orange", 1)}
                  >
                    +
                  </div>
                  <input
                    type="number"
                    value={colorQuantities.orange}
                    onChange={(e) =>
                      handleQuantityInput("orange", e.target.value)
                    }
                    min="0"
                    max="15"
                  />
                </div>
              </div>

              <div className="color-container-with-quantity cream">
                <button
                  type="button"
                  className="cream"
                  data-title={t("cream")}
                  style={{ pointerEvents: "none", cursor: "default" }}
                >
                  <span>{t("cream")}</span>
                </button>
                <div className="quantity-counter">
                  <div
                    className="quantity-decrease-button"
                    onClick={() => handleQuantityChange("cream", -1)}
                  >
                    -
                  </div>
                  <div
                    className="quantity-increase-button"
                    onClick={() => handleQuantityChange("cream", 1)}
                  >
                    +
                  </div>
                  <input
                    type="number"
                    value={colorQuantities.cream}
                    onChange={(e) =>
                      handleQuantityInput("cream", e.target.value)
                    }
                    min="0"
                    max="15"
                  />
                </div>
              </div>

              <div className="color-container-with-quantity black">
                <button
                  type="button"
                  className="black"
                  data-title={t("black")}
                  style={{ pointerEvents: "none", cursor: "default" }}
                >
                  <span>{t("black")}</span>
                </button>
                <div className="quantity-counter">
                  <div
                    className="quantity-decrease-button"
                    onClick={() => handleQuantityChange("black", -1)}
                  >
                    -
                  </div>
                  <div
                    className="quantity-increase-button"
                    onClick={() => handleQuantityChange("black", 1)}
                  >
                    +
                  </div>
                  <input
                    type="number"
                    value={colorQuantities.black}
                    onChange={(e) =>
                      handleQuantityInput("black", e.target.value)
                    }
                    min="0"
                    max="15"
                  />
                </div>
              </div>

              <div className="color-container-with-quantity pink">
                <button
                  type="button"
                  className="pink"
                  data-title={t("pink")}
                  style={{ pointerEvents: "none", cursor: "default" }}
                >
                  <span>{t("pink")}</span>
                </button>
                <div className="quantity-counter">
                  <div
                    className="quantity-decrease-button"
                    onClick={() => handleQuantityChange("pink", -1)}
                  >
                    -
                  </div>
                  <div
                    className="quantity-increase-button"
                    onClick={() => handleQuantityChange("pink", 1)}
                  >
                    +
                  </div>
                  <input
                    type="number"
                    value={colorQuantities.pink}
                    onChange={(e) =>
                      handleQuantityInput("pink", e.target.value)
                    }
                    min="0"
                    max="15"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Design Section - Only for QR Collars, not GPS Trackers */}
        {!isGPSTracker && (
          <>
            <p
              className="select-color-text"
              style={{ margin: "25px 0px 5px 10px" }}
            >
              {t("or")} {t("order your own design")}
            </p>
            <div className="separate-container">
              {/* <p className="tag-by-user-text">{t("order your own design")}</p> */}
              <p className="tag-by-user-text-warning">
                {t("custom tag by user text")}!
              </p>

              {/* Custom Design Uploads List */}
              <div className="custom-designs-list">
                {customDesigns.map((design) => (
                  <div key={design.id} className="custom-design-row">
                    <div
                      className="custom-design-image-preview-box"
                      onClick={() =>
                        document
                          .getElementById(`custom-design-${design.id}`)
                          .click()
                      }
                    >
                      {design.preview || design.image ? (
                        <img
                          src={design.preview || design.image}
                          alt="Custom design"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                      ) : (
                        <div className="upload-placeholder">
                          <svg
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ opacity: 0.5 }}
                          >
                            <path
                              d="M12 5v14M5 12h14"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          <span style={{ fontSize: "12px", color: "#666" }}>
                            {t("click to upload") || "Click to upload"}
                          </span>
                        </div>
                      )}
                      <input
                        id={`custom-design-${design.id}`}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleUpdateCustomDesignImage(design.id, file);
                          }
                        }}
                      />
                    </div>

                    <div className="custom-design-quantity">
                      <div className="quantity-counter">
                        <div
                          className="quantity-decrease-button"
                          onClick={() =>
                            handleUpdateCustomDesignQuantity(
                              design.id,
                              design.quantity - 1,
                            )
                          }
                        >
                          -
                        </div>
                        <div
                          className="quantity-increase-button"
                          onClick={() =>
                            handleUpdateCustomDesignQuantity(
                              design.id,
                              design.quantity + 1,
                            )
                          }
                        >
                          +
                        </div>
                        <input
                          type="number"
                          value={design.quantity}
                          onChange={(e) =>
                            handleUpdateCustomDesignQuantity(
                              design.id,
                              e.target.value,
                            )
                          }
                          min="0"
                          max="15"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      className="remove-custom-design-button"
                      onClick={() => handleRemoveCustomDesign(design.id)}
                      title={t("remove") || "Remove"}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#ff4444",
                      }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6l12 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              {/* Add Custom Design Button */}
              <button
                type="button"
                className="add-custom-design-button"
                onClick={handleAddCustomDesign}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 20px",
                  border: "2px dashed #ccc",
                  borderRadius: "8px",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  marginTop: customDesigns.length > 0 ? "12px" : "0",
                  width: "100%",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#999";
                  e.currentTarget.style.backgroundColor = "#f9f9f9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#ccc";
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span>{t("add custom design") || "Add Custom Design"}</span>
              </button>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <style>{`
        /* Remove any styles that might be hiding the color buttons */
      `}</style>
      <div className="order-collars-container">
        <div className="collar-order-modal-container">
          <div className="slideshow-and-imglist-and-description">
            {/* <div
              className="product-type-switcher"
              style={{
                display: "flex",
                gap: "0",
                marginBottom: "16px",
                borderRadius: "14px",
                overflow: "hidden",
                border: "1px solid #e0e0e0",
              }}
            >
              <Link
                to="/order/qrcollar"
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  textAlign: "center",
                  textDecoration: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  backgroundColor: !isGPSTracker ? "#1aba6b" : "#f5f5f5",
                  color: !isGPSTracker ? "#fff" : "#666",
                  borderRight: "1px solid #e0e0e0",
                }}
              >
                {t("qr tag")}
              </Link>
              <Link
                to="/order/gpstracker"
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  textAlign: "center",
                  textDecoration: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  backgroundColor: isGPSTracker ? "#1aba6b" : "#f5f5f5",
                  color: isGPSTracker ? "#fff" : "#666",
                }}
              >
                {t("gps tracker")}
              </Link>
            </div> */}

            <div className="slideshow-and-imglist">
              <div className="collar-manual-slideshow-container">
                <img src={images[currentImageIndex]} alt="" />

                <a className="prev" onClick={handlePreviousImage}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#0F0F0F"
                      d="M14.29 5.707a1 1 0 00-1.415 0L7.988 10.6a2 2 0 000 2.828l4.89 4.89a1 1 0 001.415-1.414l-4.186-4.185a1 1 0 010-1.415l4.182-4.182a1 1 0 000-1.414z"
                    ></path>
                  </svg>
                </a>
                <a className="next" onClick={handleNextImage}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#0F0F0F"
                      d="M9.71 18.293a1 1 0 001.415 0l4.887-4.892a2 2 0 000-2.828l-4.89-4.89a1 1 0 00-1.415 1.414l4.186 4.185a1 1 0 010 1.415L9.71 16.879a1 1 0 000 1.414z"
                    ></path>
                  </svg>
                </a>

                {/* <div className="imglist">{renderImages()}</div> */}
              </div>
              <div className="imglist">{renderImages()}</div>
            </div>

            <div className="collar-description-block-container">
              <div
                className={
                  "inside-block-1 " +
                  (isCollarDescriptionVisalbe ? "active" : "")
                }
                onClick={() =>
                  setIsCollarDescriptionVisable(!isCollarDescriptionVisalbe)
                }
                // ref={collarDescriptionRef}
              >
                <p className="title">
                  {t("how it works text1")}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#000"
                    version="1.1"
                    viewBox="0 0 100 100"
                  >
                    <path d="M78.466 35.559L50.15 63.633 22.078 35.317a2 2 0 00-2.84 2.815L48.432 67.58a1.993 1.993 0 001.727.568c.054.008.106.021.16.022a1.991 1.991 0 001.515-.576l29.447-29.196a1.999 1.999 0 10-2.815-2.839z"></path>
                  </svg>
                </p>

                <p className="tag-feature-list">{t("how it works text")}</p>
                <p className="tag-feature-list">{t("how it works text2")}</p>
                <p className="tag-feature-list under">
                  - {t("how it works text3")}
                </p>
                <p className="tag-feature-list under">
                  - {t("how it works text4")}
                </p>
              </div>

              <div
                className={
                  "inside-block-2 " + (isPetProfileVisalbe ? "active" : "")
                }
                onClick={() => setIsPetProfileVisable(!isPetProfileVisalbe)}
                // ref={petProfileRef}
              >
                <p className="title">
                  {t("pet profile")}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#000"
                    version="1.1"
                    viewBox="0 0 100 100"
                  >
                    <path d="M78.466 35.559L50.15 63.633 22.078 35.317a2 2 0 00-2.84 2.815L48.432 67.58a1.993 1.993 0 001.727.568c.054.008.106.021.16.022a1.991 1.991 0 001.515-.576l29.447-29.196a1.999 1.999 0 10-2.815-2.839z"></path>
                  </svg>
                </p>

                <p className="tag-feature-list">{t("pet profile text")}</p>
                <p className="tag-feature-list">{t("pet profile text2")}</p>
              </div>

              <div
                className={
                  "inside-block-3 " + (isTagFeaturesVisalbe ? "active" : "")
                }
                onClick={() => setIsTagFeaturesVisable(!isTagFeaturesVisalbe)}
                // ref={tagFeaturesRef}
              >
                <p className="title">
                  {t("tag features")}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#000"
                    version="1.1"
                    viewBox="0 0 100 100"
                  >
                    <path d="M78.466 35.559L50.15 63.633 22.078 35.317a2 2 0 00-2.84 2.815L48.432 67.58a1.993 1.993 0 001.727.568c.054.008.106.021.16.022a1.991 1.991 0 001.515-.576l29.447-29.196a1.999 1.999 0 10-2.815-2.839z"></path>
                  </svg>
                </p>

                <ul>
                  <li>{t("waterproof")}</li>

                  <li>{t("scratch, bite, and fade resistant")}</li>

                  <li>{t("light weight and jingle free")}</li>

                  <li>
                    {t("no batteries needed (tag will last you decades!)")}
                  </li>

                  <li>{t("compatible with all smartphones")}</li>

                  <li>{t("no monthly fee")}</li>

                  <li>{t("can be used anywhere and anytime")}</li>
                </ul>
              </div>

              <div
                className={
                  "inside-block-4 " + (isCollarSizeFitVisalbe ? "active" : "")
                }
                onClick={() =>
                  setIsCollarSizeFitVisable(!isCollarSizeFitVisalbe)
                }
                // ref={collarSizeFitRef}
              >
                <p className="title">
                  {t("size and fit")}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#000"
                    version="1.1"
                    viewBox="0 0 100 100"
                  >
                    <path d="M78.466 35.559L50.15 63.633 22.078 35.317a2 2 0 00-2.84 2.815L48.432 67.58a1.993 1.993 0 001.727.568c.054.008.106.021.16.022a1.991 1.991 0 001.515-.576l29.447-29.196a1.999 1.999 0 10-2.815-2.839z"></path>
                  </svg>
                </p>

                <p className="tag-feature-list">
                  {t("small")} - 25 {t("mm")} &emsp; ({" "}
                  {t("for cats and little dogs")} )
                </p>
                <p className="tag-feature-list">
                  {t("medium")} - 30 {t("mm")} &emsp; ({" "}
                  {t("for medium size dogs")} )
                </p>
                <p className="tag-feature-list">
                  {t("big")} - 35 {t("mm")} &emsp; ( {t("for big size dogs")} )
                </p>
              </div>
            </div>
          </div>

          <div className="collar-header-and-options-container">
            {/* <div className="separate-container">
            <p className="checkout-info-text">{t("checkout info text")}</p>
          </div> */}

            {/* Product Type Switcher */}

            <div className="collar-header">
              <p>{isGPSTracker ? t("gps tracker") : t("qr tag")}</p>
              <Link onClick={goBack} className="close_login">
                <span>{t("close modal")}</span>
                &times;
              </Link>
            </div>

            <p className="tag-price-in-title">
              {unitPrice} {t("gel")}
            </p>

            <ul className="collar-header-tag-priority-list">
              <li>🐶 {t("pet's profile update")}</li>
              <li>📍 {t("gps notification")}</li>
              <li>🔒 {t("privacy control")}</li>
              <li>🪫 {t("no batteries needed")}</li>
            </ul>

            <div className="warranty">
              <div className="warranty-title-container">
                <svg
                  role="presentation"
                  fill="none"
                  focusable="false"
                  strokeWidth="2"
                  className="icon icon-picto-love"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 5.457C6.823-.895 1.358 3.619 1.286 8.484c0 7.24 8.665 13.185 10.714 13.185 2.049 0 10.714-5.946 10.714-13.187C22.642 3.617 17.177-.895 12 5.457Z"
                    fill="currentColor"
                    fillOpacity=".12"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
                <p>{t("warranty title text")}</p>
              </div>
              <div className="warranty-main-text-container">
                <p>{t("warranty main text")}</p>
              </div>
            </div>

            {/* <div className="qr-without-collar">
              <DropDownInput
                name="choose a collar"
                fieldName="with_collar"
                options={["metal tag", "GPS tracker", "GPS live locator"]}
                handleOptionClick={setValue}
                customClassNameWrapper="custom-wrapper"
                customClassNameSelectBtn="custom-select-btn"
                customClassNameSelecMenuDropDown="custom-drop-down"
              />
            </div> */}

            {renderColor()}

            {/* <button
              type="button"
              className="order-button open-cart-btn"
              disabled={
                Object.values(colorQuantities).reduce((s, q) => s + q, 0) +
                  customDesigns
                    .filter((d) => d.image)
                    .reduce((s, d) => s + (d.quantity || 0), 0) ===
                0
              }
              onClick={() => dispatch(setCartOpen(true))}
            >
              {t("go to checkout")}
            </button> */}

            {totalSelectedItems > 0 && (
              <Link className="order-button open-cart-btn" to="/checkout">
                {t("place order")}
              </Link>
            )}

            {/* <div className="separate-container">
            {renderSizeFields()}
            <p className="qr-collar-price">
              <span className="quantity-container">
                <span>{t("quantity")}:</span>
                <DropDownInput
                  name="0"
                  fieldName="quantity"
                  options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]}
                  handleOptionClick={setValue}
                  customClassNameWrapper="custom-wrapper-quantity"
                  customClassNameSelectBtn="custom-select-btn-quantity"
                  customClassNameSelecMenuDropDown="custom-drop-down-quantity"
                />
              </span>
            </p>
          </div> */}

            <p className="shipping-title">{t("shipping to georgia")}</p>

            <div className="shipping">
              <div className="shipping-header-flex">
                <FontAwesomeIcon icon={faTruckFast} />
                <p className="delivery">{t("delivery")}</p>
              </div>

              <div className="inside-container">
                <p className="delivery-prices">
                  - {t("delivery description 1")}
                </p>
                {/* <p className="delivery-prices">
                  - {t("delivery description 2")}
                </p> */}
                <p className="delivery-prices">
                  - {t("delivery description 3")}
                </p>
                {/* <p className="delivery-report">{t("call us")} - 591 09 80 80</p> */}
              </div>
            </div>

            {/* <div className="shipping">
              <div className="shipping-header-flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  stroke="#000"
                  strokeWidth="20.48"
                  className="not-fill"
                  viewBox="-20.48 -20.48 1064.96 1064.96"
                >
                  <g>
                    <path d="M488.006 999.898C218.982 999.898.106 781.022.106 512c0-269.022 218.876-487.898 487.9-487.898 156.624 0 304.81 76.03 396.386 203.364a8.016 8.016 0 01-1.828 11.17c-3.61 2.562-8.592 1.766-11.17-1.828C782.818 113.63 639.49 40.1 488.006 40.1 227.792 40.098 16.104 251.788 16.104 512c0 260.21 211.69 471.902 471.902 471.902 260.21 0 471.902-211.692 471.902-471.902a7.992 7.992 0 017.998-7.998 7.994 7.994 0 017.998 7.998c0 269.022-218.876 487.898-487.898 487.898z"></path>
                    <path d="M1015.896 567.988a7.976 7.976 0 01-5.656-2.344l-47.99-47.99a7.996 7.996 0 1111.31-11.31l47.99 47.992a7.994 7.994 0 010 11.308 7.96 7.96 0 01-5.654 2.344z"></path>
                    <path d="M919.916 567.988a7.994 7.994 0 01-5.656-13.652l47.99-47.992a7.996 7.996 0 1111.31 11.31l-47.99 47.99a7.96 7.96 0 01-5.654 2.344zm-191.96 167.966H280.048c-4.422 0-8-3.578-8-7.998V376.028a7.994 7.994 0 018-7.998h447.906c4.422 0 8 3.578 8 7.998v351.928a7.994 7.994 0 01-7.998 7.998zm-439.91-15.996h431.91V384.026h-431.91v335.932z"></path>
                    <path d="M727.956 384.026H280.048a8.002 8.002 0 01-7.39-4.936 8.018 8.018 0 011.734-8.716l63.988-63.986a7.992 7.992 0 015.656-2.344H663.97c2.124 0 4.154.844 5.654 2.344l63.988 63.986a8.02 8.02 0 011.734 8.716 8.002 8.002 0 01-7.39 4.936zM299.358 368.03h409.29l-47.99-47.99H347.346l-47.988 47.99z"></path>
                    <path d="M456.012 384.026a8.008 8.008 0 01-7.156-11.576l31.994-63.988c2-3.936 6.78-5.546 10.732-3.578a8.006 8.006 0 013.578 10.732l-31.994 63.986a8.014 8.014 0 01-7.154 4.424zm95.98 0a8.016 8.016 0 01-7.154-4.42l-31.994-63.986a8.006 8.006 0 013.578-10.732 7.992 7.992 0 0110.732 3.578l31.992 63.988a8.006 8.006 0 01-3.578 10.732 7.992 7.992 0 01-3.576.84z"></path>
                    <path d="M456.012 448.014a7.994 7.994 0 01-7.998-8v-63.988c0-4.42 3.578-7.998 7.998-7.998s7.998 3.578 7.998 7.998v63.988c0 4.422-3.578 8-7.998 8z"></path>
                    <path d="M551.992 448.014h-95.98c-4.42 0-7.998-3.578-7.998-8s3.578-7.998 7.998-7.998h95.98c4.42 0 7.998 3.578 7.998 7.998s-3.578 8-7.998 8z"></path>
                    <path d="M551.992 448.014a7.994 7.994 0 01-7.998-8v-63.988a7.994 7.994 0 017.998-7.998 7.994 7.994 0 017.998 7.998v63.988c0 4.422-3.578 8-7.998 8zm-95.98 7.998a7.994 7.994 0 01-7.998-7.998v-8c0-4.42 3.578-7.998 7.998-7.998s7.998 3.578 7.998 7.998v8a7.994 7.994 0 01-7.998 7.998zm31.994 0c-4.422 0-8-3.578-8-7.998v-8a7.994 7.994 0 018-7.998 7.994 7.994 0 017.998 7.998v8a7.994 7.994 0 01-7.998 7.998zm31.992 0a7.994 7.994 0 01-7.998-7.998v-8a7.994 7.994 0 017.998-7.998c4.422 0 8 3.578 8 7.998v8a7.994 7.994 0 01-8 7.998z"></path>
                    <path d="M551.992 456.012a7.994 7.994 0 01-7.998-7.998v-8a7.994 7.994 0 017.998-7.998 7.994 7.994 0 017.998 7.998v8a7.994 7.994 0 01-7.998 7.998zm143.97 247.948h-31.994a7.994 7.994 0 01-7.998-8v-31.992c0-4.422 3.578-8 7.998-8h31.994a7.994 7.994 0 017.998 8v31.992c0 4.422-3.578 8-7.998 8zm-23.996-15.998h15.996v-15.996h-15.996v15.996zm-39.992 15.998h-31.992a7.994 7.994 0 01-7.998-8v-31.992c0-4.422 3.576-8 7.998-8h31.992c4.422 0 8 3.578 8 8v31.992c0 4.422-3.578 8-8 8zm-23.994-15.998h15.996v-15.996H607.98v15.996zm-135.972-15.996H312.04a7.994 7.994 0 01-7.998-7.998c0-4.422 3.578-8 7.998-8h159.968a7.994 7.994 0 017.998 8 7.994 7.994 0 01-7.998 7.998zm-79.982 31.994h-79.984a7.994 7.994 0 01-7.998-8 7.994 7.994 0 017.998-7.998h79.984a7.994 7.994 0 017.998 7.998c0 4.422-3.578 8-7.998 8z"></path>
                  </g>
                </svg>
                <p className="delivery">{t("return policy")}</p>
              </div>

              <div className="inside-container">
                <p className="delivery-prices">
                  <Link to="/returnpolicy">{t("return policy")}</Link>
                </p>
                <p className="delivery-report">{t("call us")} - 591 09 80 80</p>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderQRCorall;
